import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import sadCancelOrderIcon from '@/assets/common/sadCancelOrder.svg';

import Modal from '../Modal';

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reasonText: string) => void;
}

const reasons = [
  { value: 'error', label: 'Generé la guía por error.' },
  { value: 'wrong_data', label: 'Los datos de la guía están erróneos.' },
  { value: 'high_cost', label: 'El costo fue más de lo que esperaba.' },
  { value: 'not_picked_up', label: 'No han pasado por el paquete.' },
  { value: 'not_delivered', label: 'El paquete no ha sido entregado.' },
  { value: 'other', label: 'Otro' },
];

const MIN_REASON_LENGTH = 5;

const CancelOrderModal = ({ isOpen, onClose, onConfirm }: CancelOrderModalProps) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [error, setError] = useState('');

  const isOther = selectedReason === 'other';
  const isCustomReasonValid = customReason.trim().length >= MIN_REASON_LENGTH;

  const resetState = () => {
    setSelectedReason('');
    setCustomReason('');
    setError('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedReason(value);
    setError('');
    if (value !== 'other') setCustomReason('');
  };

  const handleCustomReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomReason(value);
    if (value.trim().length >= MIN_REASON_LENGTH) setError('');
  };

  const handleConfirm = () => {
    if (!selectedReason) {
      setError('Por favor selecciona un motivo');
      return;
    }

    if (isOther && !isCustomReasonValid) {
      setError('El motivo debe tener al menos 5 caracteres');
      return;
    }

    // Obtener el texto completo del motivo
    const reasonText = isOther
      ? customReason.trim()
      : reasons.find((reason) => reason.value === selectedReason)?.label || '';

    onConfirm?.(reasonText);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <img src={sadCancelOrderIcon} alt="Cancelar orden" style={{ width: 100, height: 100 }} />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ¿Estás seguro de que deseas cancelar tu orden?
          </Typography>
          <Typography variant="body1">
            Queremos escucharte, a continuación dinos por qué.
          </Typography>
        </Box>

        <FormControl error={!!error}>
          <RadioGroup name="cancel-reason" value={selectedReason} onChange={handleReasonChange}>
            {reasons.map(({ value, label }) => (
              <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
            ))}
          </RadioGroup>

          {isOther && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Especifica el motivo"
              value={customReason}
              onChange={handleCustomReasonChange}
              error={!!error}
              helperText={error || `Mínimo ${MIN_REASON_LENGTH} caracteres requeridos`}
              sx={{ mt: 2 }}
              placeholder="Describe el motivo de cancelación..."
            />
          )}

          {!isOther && error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </FormControl>

        <Alert severity="warning" sx={{ textAlign: 'center' }}>
          Una vez cancelada, la orden no se podrá recuperar.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={!selectedReason || (isOther && !isCustomReasonValid)}
          >
            Confirmar cancelación
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CancelOrderModal;
